/**
 * Global front-end script.
 *
 * Compiled to dist/assets/js/global.js and enqueued on both the front end and
 * in the editor — but the editor takes the early-exit branch below, so none of
 * the scroll or animation machinery runs there.
 *
 * GSAP, ScrollTrigger and ScrollToPlugin are loaded separately (see
 * wpbase_enqueue_assets in inc/general.php) and referenced as globals.
 */

const AOS = require("aos");
const Lenis = require("lenis").default || require("lenis");
require("select2");

// Must stay in step with $header-breakpoint in abstracts/_mixins.scss.
const HEADER_BREAKPOINT = 992;

function isWordPressEditor() {
  return (
    typeof window !== "undefined" &&
    (window.location.pathname.includes("/wp-admin/") ||
      window.frameElement !== null ||
      document.body.classList.contains("block-editor-page") ||
      document.getElementById("editor") !== null ||
      (typeof wp !== "undefined" && wp.blocks !== undefined))
  );
}

if (isWordPressEditor()) {
  // ── Editor ──────────────────────────────────────────────────────────────
  // Block previews can contain <select> elements that should look like they do
  // on the front end. Nothing else here belongs in the editor.

  jQuery(document).ready(function ($) {
    const selectOptions = {
      minimumResultsForSearch: Infinity,
      placeholder: "Select",
    };

    // Scoped to previews so ACF's own field UI is left alone.
    $(".acf-block-preview select").select2(selectOptions);

    if (window.acf) {
      window.acf.addAction("render_block_preview", function () {
        // A short delay lets ACF finish swapping the preview markup in.
        setTimeout(function () {
          $(".acf-block-preview select")
            .not(".select2-hidden-accessible")
            .select2(selectOptions);
        }, 100);
      });
    }
  });
} else {
  // ── Front end ───────────────────────────────────────────────────────────

  document.addEventListener("DOMContentLoaded", function () {
    // ── Lenis smooth scroll ───────────────────────────────────────────────
    // Skipped on mobile (native momentum scrolling is better there) and when
    // the user has asked for reduced motion.

    const isMobile = window.innerWidth < 768;
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    let lenis = null;

    if (!isMobile && !prefersReducedMotion) {
      lenis = new Lenis({
        lerp: 0.1,
        smoothWheel: true,
        wheelMultiplier: 1,
      });

      if (typeof gsap !== "undefined") {
        // Drive Lenis from GSAP's ticker so the page runs one RAF loop, not two.
        gsap.ticker.add((time) => {
          lenis.raf(time * 1000);
        });
        gsap.ticker.lagSmoothing(0);
      } else {
        const raf = (time) => {
          lenis.raf(time);
          requestAnimationFrame(raf);
        };
        requestAnimationFrame(raf);
      }
    }

    // Blocks that need scroll position can read window.lenis.
    window.lenis = lenis;

    // ── Sticky header ─────────────────────────────────────────────────────
    // The header only pins once the user is well past it, then slides in. The
    // hide threshold is much lower than the show threshold so scrolling up and
    // down around one point cannot make it flicker.

    const header = document.querySelector(".header");

    if (header) {
      let headerHeight = 0;
      let isSticky = false;
      let headerHeightUpdatePending = false;

      // The header is out of flow from the start (position: absolute, see
      // layout/_header.scss), so pinning it moves nothing and there is no gap
      // to reserve. The height is only read to set the show threshold.
      function measureHeaderHeight() {
        // jQuery's slide writes an inline height, so measuring mid-animation
        // would read the wrong value — the last good one still stands.
        if (jQuery(header).is(":animated")) return;

        const measured = header.offsetHeight;

        if (measured) {
          headerHeight = measured;
        }
      }

      function updateHeaderHeight() {
        if (headerHeightUpdatePending) return;
        headerHeightUpdatePending = true;

        requestAnimationFrame(() => {
          measureHeaderHeight();
          headerHeightUpdatePending = false;
        });
      }

      function updateStickyHeader(scrollY) {
        if (!headerHeight) {
          measureHeaderHeight();
        }

        const showThreshold = headerHeight + 300;
        const hideThreshold = 300;

        if (scrollY > showThreshold) {
          if (!isSticky) {
            isSticky = true;

            header.classList.add("header--sticky");
            header.classList.remove("header--show");

            jQuery(header)
              .stop(true, true)
              .hide()
              .slideDown(280, function () {
                header.style.display = "";

                // stop(true, true) fires this callback early, so it can land
                // after the header has already been released again.
                if (!isSticky) return;

                header.classList.add("header--show");
              });
          }
        } else if (scrollY < hideThreshold && isSticky) {
          isSticky = false;

          jQuery(header)
            .stop(true, true)
            .slideUp(220, function () {
              header.style.display = "";

              // Re-pinned before the slide finished — leave it pinned.
              if (isSticky) return;

              header.classList.remove("header--show", "header--sticky");
            });
        }
      }

      measureHeaderHeight();

      if (lenis) {
        lenis.on("scroll", (instance) => {
          updateStickyHeader(instance.animatedScroll);
        });
      } else {
        window.addEventListener(
          "scroll",
          () => updateStickyHeader(window.scrollY),
          { passive: true },
        );
      }

      window.addEventListener(
        "resize",
        () => {
          updateHeaderHeight();
          updateStickyHeader(lenis ? lenis.animatedScroll : window.scrollY);
        },
        { passive: true },
      );

      updateStickyHeader(window.scrollY);
    }

    // ── Mobile menu ───────────────────────────────────────────────────────
    // `show-mobile-menu` on <body> drives the drawer; see layout/_header.scss.

    const menuToggles = document.querySelectorAll(".header__menu-toggle");

    if (menuToggles.length) {
      menuToggles.forEach((toggle) => {
        toggle.addEventListener("click", function () {
          const isOpen = document.body.classList.toggle("show-mobile-menu");

          menuToggles.forEach((t) => {
            t.setAttribute("aria-expanded", isOpen ? "true" : "false");
          });
        });
      });

      // Close on Escape, and on resize back up to the full nav — otherwise the
      // drawer state persists into a layout that has no way to dismiss it.
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && document.body.classList.contains("show-mobile-menu")) {
          document.body.classList.remove("show-mobile-menu");
          menuToggles.forEach((t) => t.setAttribute("aria-expanded", "false"));
        }
      });

      window.addEventListener(
        "resize",
        () => {
          if (window.innerWidth > HEADER_BREAKPOINT) {
            document.body.classList.remove("show-mobile-menu");
            menuToggles.forEach((t) => t.setAttribute("aria-expanded", "false"));
          }
        },
        { passive: true },
      );
    }

    // ── Scroll animations ─────────────────────────────────────────────────
    // Opt in from markup:
    //   data-speed="0.5"  parallax, relative to scroll
    //   data-float="20"   idle float, in px
    //   data-reveal       fade and rise into view

    if (typeof gsap !== "undefined") {
      gsap.registerPlugin(ScrollTrigger);

      // Keep ScrollTrigger's positions in step with Lenis's virtual scroll.
      if (lenis) {
        lenis.on("scroll", ScrollTrigger.update);
      }

      gsap.utils.toArray("[data-speed]").forEach((element) => {
        const speed = parseFloat(element.dataset.speed) || 0.5;

        gsap.to(element, {
          yPercent: -50 * speed,
          ease: "none",
          scrollTrigger: {
            trigger: element,
            start: "top bottom",
            end: "bottom top",
            scrub: 1.5,
          },
        });
      });

      gsap.utils.toArray("[data-float]").forEach((element) => {
        const intensity = parseFloat(element.dataset.float) || 20;

        gsap.to(element, {
          y: `random(-${intensity}, ${intensity})`,
          rotation: "random(-2, 2)",
          duration: "random(3, 5)",
          ease: "sine.inOut",
          yoyo: true,
          repeat: -1,
          delay: "random(0, 2)",
        });
      });

      gsap.utils.toArray("[data-reveal]").forEach((element) => {
        gsap.fromTo(
          element,
          { opacity: 0, y: 50, scale: 0.98 },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 1.2,
            ease: "power2.out",
            scrollTrigger: {
              trigger: element,
              start: "top 85%",
              end: "bottom 15%",
              toggleActions: "play none none reverse",
            },
          },
        );
      });
    } else {
      console.warn("GSAP not loaded — scroll animations disabled");
    }

    // ── Smooth anchor scrolling ───────────────────────────────────────────
    // Offsets the target so it does not land underneath the sticky header.

    let cachedHeaderHeight = 0;

    function updateCachedHeaderHeight() {
      const h = document.querySelector("header");
      if (h) cachedHeaderHeight = h.offsetHeight;
    }

    requestAnimationFrame(updateCachedHeaderHeight);
    window.addEventListener(
      "resize",
      () => requestAnimationFrame(updateCachedHeaderHeight),
      { passive: true },
    );

    document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
      const href = anchor.getAttribute("href");

      // "#" alone is a placeholder link, not an anchor target.
      if (!href || href === "#") return;

      anchor.addEventListener("click", function (e) {
        const targetElement = document.querySelector(href);

        if (!targetElement) return;

        e.preventDefault();

        const targetPosition =
          targetElement.getBoundingClientRect().top +
          window.scrollY -
          cachedHeaderHeight -
          20;

        if (lenis) {
          lenis.scrollTo(targetPosition, { duration: 1.5 });
        } else if (typeof gsap !== "undefined" && gsap.plugins.ScrollToPlugin) {
          gsap.to(window, {
            duration: 1.5,
            scrollTo: { y: targetPosition, autoKill: false },
            ease: "power2.inOut",
          });
        } else {
          window.scrollTo({ top: targetPosition, behavior: "smooth" });
        }
      });
    });
  });

  // ── Select2 ─────────────────────────────────────────────────────────────

  jQuery(document).ready(function ($) {
    $("select").select2({
      minimumResultsForSearch: Infinity,
      placeholder: "Select",
    });

    // Let the dropdown scroll natively instead of fighting Lenis.
    $(document).on("select2:open", () => {
      const dropdown = document.querySelector(".select2-dropdown");
      if (dropdown) dropdown.setAttribute("data-lenis-prevent", "");
    });

    // Select2 draws its own arrow; swap in the theme's chevron. currentColor
    // means it picks up whatever colour the selection box is using.
    const customSvgArrow = `
      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="6" viewBox="0 0 10 6" aria-hidden="true" focusable="false">
        <path d="M1.2 0.7 5 4.5 8.8 0.7a.65.65 0 1 1 .9.9l-4.25 4.25a.65.65 0 0 1-.9 0L0.3 1.6a.65.65 0 0 1 .9-.9Z" fill="currentColor"/>
      </svg>
    `;

    // Deferred so Select2 has finished building its markup.
    setTimeout(function () {
      $(
        ".select2-container--default .select2-selection--single, .select2-container--default .select2-selection--multiple",
      ).each(function () {
        const wrapper = document.createElement("div");
        wrapper.innerHTML = customSvgArrow;
        wrapper.style.cssText =
          "position:absolute;top:50%;right:12px;transform:translateY(-50%);display:flex;align-items:center;justify-content:center;pointer-events:none;";

        this.style.position = "relative";
        this.appendChild(wrapper);
      });
    }, 100);
  });

  // ── AOS ─────────────────────────────────────────────────────────────────
  // Initialised on `load` rather than DOMContentLoaded so images have sized
  // and AOS measures the real document height.

  jQuery(window).on("load", function () {
    AOS.init({
      duration: 700,
      once: true,
    });

    window.AOS = AOS;
  });
}
